import { NgModule } from "@angular/core";
import { PreloadAllModules, RouterModule, Routes } from "@angular/router";
import { AuthFailureGuard, AuthLogoutGuard } from "@evc/platform";
import { OfflineReportTemplateComponent } from "./_offline/component/offline-report-template/offline-report-template.component";
import { featureFlagGuard } from "./_offline/guard/feature-flag.guard";
import { VisolAppAdminGuard } from "./_offline/guard/visol-app-admin.guard";
import { CanDeactivateGuard } from "./_shared/guard/can-deactivate.guard";
import { OffineAccessGuard } from "./_shared/guard/offline-access.guard";
import { EmbeddedReportComponent } from "./analytics/analytics-powerbi-report/analytics-powerbi-report";
import { RoutePage, RouteSettings } from "./app-routes.enum";
import { FEATURE_FLAGS } from "./app.constants";
import { AssetTypePageComponent } from "./asset-type-page/asset-type-page.component";
import { AuthFailComponent } from "./auth-fail/auth-fail.component";
import { InspectionListComponent } from "./inspection-list/inspection-list.component";
import { InspectionReportListComponent } from "./inspection-report-list/inspection-report-list.component";
import { InspectionComponent } from "./inspection/inspection.component";
import { LiveAssetPageComponent } from "./live-asset-page/live-asset-page.component";
import { LoginComponent } from "./login/login.component";
import { SettingsMarksEditDialogComponent, SettingsTitlesEditDialogComponent } from "./settings/settings-mark-title/settings-mark-title.component";
import { SettingsReportTemplateComponent } from "./settings/settings-report-template/settings-report-template.component";
import { SettingsSubjectComponent } from "./settings/settings-subject/settings-subject.component";
import { SettingsTagsEditDialogComponent } from "./settings/settings-tags/settings-tags.component";
import { SettingsTenantComponent } from "./settings/settings-tenant/settings-tenant.component";
import { SettingsComponent } from "./settings/settings.component";
import { ASSET_TYPES_ROUTES, ASSET_TYPE_ROUTE, LIVE_ASSET_ROUTE, LIVE_ASSETS_ROUTES } from "@rvi/asset-modules";

const routes: Routes = [
  {
    path: RoutePage.InspectionList,
    canActivate: [OffineAccessGuard],
    component: InspectionListComponent,
    runGuardsAndResolvers: "always"
  },
  {
    path: RoutePage.InspectionReportList,
    canActivate: [OffineAccessGuard],
    component: InspectionReportListComponent
  },
  {
    path: RoutePage.Inspection,
    canActivate: [OffineAccessGuard],
    component: InspectionComponent
  },
  {
    path: ASSET_TYPE_ROUTE,
    canActivate: [OffineAccessGuard, featureFlagGuard(FEATURE_FLAGS.ASSET_MODULE)],
    component: AssetTypePageComponent,
    children: ASSET_TYPES_ROUTES
  },
  {
    path: LIVE_ASSET_ROUTE,
    canActivate: [OffineAccessGuard, featureFlagGuard(FEATURE_FLAGS.ASSET_MODULE)],
    component: LiveAssetPageComponent,
    children: LIVE_ASSETS_ROUTES
  },
  {
    path: RoutePage.InspectionDataManagement,
    canActivate: [OffineAccessGuard],
    children: [
      {
        path: "**",
        component: InspectionComponent
      }
    ]
  },
  {
    path: `${RoutePage.InspectionReport}/:inspectionId/:reportId`,
    canActivate: [OffineAccessGuard],
    component: InspectionComponent
  },
  {
    path: RoutePage.Settings,
    canActivate: [VisolAppAdminGuard, OffineAccessGuard],
    canActivateChild: [VisolAppAdminGuard, OffineAccessGuard],
    component: SettingsComponent,
    children: [
      {
        path: RouteSettings.Subject,
        component: SettingsSubjectComponent
      },
      {
        path: RouteSettings.ReportTemplateList,
        component: SettingsReportTemplateComponent
      },
      {
        path: RouteSettings.OfflineReportTemplate,
        component: OfflineReportTemplateComponent
      },
      {
        path: RouteSettings.Mark,
        canDeactivate: [CanDeactivateGuard],
        component: SettingsMarksEditDialogComponent,
        data: { showSideNav: true }
      },
      {
        path: RouteSettings.Title,
        canDeactivate: [CanDeactivateGuard],
        component: SettingsTitlesEditDialogComponent,
        data: { showSideNav: true }
      },
      {
        path: RouteSettings.Tags,
        canDeactivate: [CanDeactivateGuard],
        component: SettingsTagsEditDialogComponent,
        data: { showSideNav: true }
      },
      {
        path: RouteSettings.TenantConfig,
        canDeactivate: [CanDeactivateGuard],
        component: SettingsTenantComponent,
        data: { showSideNav: true }
      },
      {
        path: "",
        canDeactivate: [CanDeactivateGuard],
        redirectTo: `/${RoutePage.Settings}/${RouteSettings.Mark}`,
        pathMatch: "full"
      }
    ]
  },
  {
    path: RoutePage.AnalyticsPowerBiReport,
    canActivate: [OffineAccessGuard],
    component: EmbeddedReportComponent
  },
  {
    path: `${RoutePage.AnalyticsPowerBiReport}/:bookmarkId`,
    canActivate: [OffineAccessGuard],
    component: EmbeddedReportComponent
  },
  {
    path: RoutePage.IndexPage,
    redirectTo: RoutePage.InspectionList,
    pathMatch: "full"
  },
  {
    path: RoutePage.Login,
    canActivate: [AuthFailureGuard],
    component: LoginComponent,
    pathMatch: "full"
  },
  {
    path: RoutePage.AuthenticationFailure,
    canActivate: [AuthFailureGuard],
    loadComponent: () => AuthFailComponent
  },
  {
    path: RoutePage.Logout,
    canActivate: [AuthLogoutGuard],
    data: { logout: true },
    component: AuthFailComponent
  },
  {
    path: RoutePage.CancelLogin,
    redirectTo: RoutePage.InspectionList
  }
];

@NgModule({
  imports: [
    RouterModule.forRoot(routes, {
      preloadingStrategy: PreloadAllModules,
      onSameUrlNavigation: "reload"
    })
  ],
  exports: [RouterModule],
  providers: [CanDeactivateGuard, OffineAccessGuard]
})
export class AppRoutingModule {}
